import { Injectable } from '@angular/core';

import { FixtureType } from '@cds/betting-offer';
import { TagType } from '@cds/betting-offer/tags';
import { CountsFixturesRequest, FixtureRequest, FixtureState, OfferMapping, SortByCriteria } from '@cds/query-objects';
import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { PrematchNavigationConfig, TournamentsDataConfig } from '@frontend/sports/common/client-config-data-access';
import { RegionConstant, SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { CountItem, sportModelMethods } from '@frontend/sports/common/core/data-access/sport-model';
import { Tournament } from '@frontend/sports/types/components/tournaments';
import { UserService } from '@frontend/sports/user/feature';
import { SessionStoreService } from '@frontend/vanilla/core';
import { sumBy, toString } from 'lodash-es';
import { EMPTY, Observable, filter, map, of, startWith, switchMap, timer } from 'rxjs';

import { SportsCacheService } from '../../client-caching/sports-cache.service';
import { LeagueTreeItem } from '../../competition-list/competition-list.models';
import {
    BaseTreeItem,
    GroupedEvents,
    LeftNavType,
    NavEventModel,
    TreeItem,
    TreeItemType,
} from '../../event-details-common/event-details-common.models';
import { getLeagueId } from '../../event-details-common/event-details-common.utils';
import { NavigationEventMapperService } from '../navigation-mapper/navigation-event-mapper.service';
import { PrematchNavState } from './prematch-nav-state';

export interface LeagueParameters {
    leagueIds: number[];
    virtualLeagueIds: number[];
}

@Injectable({ providedIn: 'root' })
export class PreMatchEventsNavigationService {
    private expandedLeagues = new Map<number, boolean>();
    private sessionStoreKey = 'prematch-navigation-state';
    private isNavigationExpanded: boolean;

    // The initialEvent is needed for case when on refresh there are no events for the region and sport
    initialEvent: EventModel;
    isEventSelectedFromPrematch = false;
    state: PrematchNavState;

    constructor(
        private bettingService: BettingOfferApi,
        private navigationConfig: PrematchNavigationConfig,
        private store: SessionStoreService,
        private cacheService: SportsCacheService,
        private tournamentsConfig: TournamentsDataConfig,
        private eventMapperService: NavigationEventMapperService,
        private user: UserService,
    ) {
        this.state = this.store.get(this.sessionStoreKey) || new PrematchNavState();
    }

    getNavigationTree(event: EventModel): Observable<BaseTreeItem> {
        this.initialEvent = event;
        this.isNavigationExpanded = true;

        return this.getTree().pipe(
            map((tree) => {
                const region = tree.children[0];
                if (region.children.length) {
                    const leagueExists = region.children.some((c) => c.id === getLeagueId(event));
                    if (leagueExists) {
                        const selectedLeague = getLeagueId(event);
                        this.applyThreshold(region, selectedLeague);
                    }
                }

                return tree;
            }),
        );
    }

    getObservableTree(): Observable<BaseTreeItem> {
        const interval = this.navigationConfig.pollingInterval * 1000;

        if (!interval) {
            return EMPTY;
        }

        return this.user.onAuthenticationChange$.pipe(
            map(() => 0),
            startWith(interval),
            switchMap((due) => timer(due, interval)),
            switchMap(() => (this.isNavigationExpanded && this.expandedLeagues.size > 0 ? this.getTreeWithEvents() : this.getTree())),
        );
    }

    getEventsForLeagues(leagueParameters: LeagueParameters): Observable<GroupedEvents> {
        if (!leagueParameters.leagueIds.length && !leagueParameters.virtualLeagueIds.length) {
            return of({});
        }

        const request: FixtureRequest = {
            fixtureTypes: FixtureType.Standard,
            state: FixtureState.Latest,
            offerMapping: OfferMapping.MainMarkets,
            sortBy: SortByCriteria.FixtureStage,
            competitionIds: toString(leagueParameters.leagueIds),
            virtualCompetitionIds: toString(leagueParameters.virtualLeagueIds),
        };

        return this.bettingService.getFixtureList(request).pipe(map((page) => this.eventMapperService.mapFixtures(page)));
    }

    getSportTree(sportId: number): Observable<{ sport: CountItem; tournaments: Tournament[] } | undefined> {
        return this.cacheService.getSport(sportId).pipe(
            map((sport) => {
                if (!sport) {
                    return;
                }

                return {
                    sport,
                    tournaments: this.getValidTournaments(sportId),
                };
            }),
        );
    }

    toggleLeague(leagueId: number, isVirtual: boolean): void {
        if (this.expandedLeagues.has(leagueId)) {
            this.expandedLeagues.delete(leagueId);
        } else {
            this.expandedLeagues.set(leagueId, isVirtual);
        }
    }

    toggleRegion(): void {
        this.isNavigationExpanded = !this.isNavigationExpanded;
    }

    toggleMarkets(): void {
        this.state.showMarkets = !this.state.showMarkets;
        this.store.set(this.sessionStoreKey, this.state);
    }

    mapNavEvents(events: EventModel[]): NavEventModel[] {
        return events.map((event) => ({
            data: event,
            showMarkets: this.state.showMarkets,
            selected: false,
        }));
    }

    private fetchTree(): Observable<CountItem[]> {
        return this.bettingService
            .getPrematchEventTree(this.initialEvent.region.id, this.initialEvent.sport.id)
            .pipe(map((result) => sportModelMethods.fromTag(result)));
    }

    private getTree(): Observable<BaseTreeItem> {
        return this.fetchTree().pipe(map((tree) => this.mapTree(tree)));
    }

    private mapTree(countItems: CountItem[]): BaseTreeItem {
        const hasEvents = countItems && countItems.length > 0 && countItems[0].children.length > 0;
        const region = this.mapRegion(hasEvents);

        if (hasEvents) {
            const regionItem = countItems[0].children[0];
            regionItem.children.forEach((l) => region.children.push(this.mapLeague(l)));
            region.count = sumBy(regionItem.children, (l) => l.counts.preMatch + l.counts.live);
        }

        return {
            children: [region],
        };
    }

    private applyThreshold(region: TreeItem, selectedLeagueId: number): void {
        const exceedsTreshold = sumBy(region.children, (l) => l.count!) > this.navigationConfig.treshold;
        region.children.forEach((league) => {
            league.expanded = !exceedsTreshold || league.id === selectedLeagueId;
            if (league.expanded) {
                this.toggleLeague(league.id, !!league.isVirtual);
            }
        });
    }

    private mapRegion(hasEvents: boolean): TreeItem {
        const regionName = this.initialEvent.region.name;
        const regionFlag = this.initialEvent.sport.id === SportConstant.Tennis ? RegionConstant.World : this.initialEvent.region.id;

        return {
            id: this.initialEvent.region.id,
            name: regionName, // If sport is Tennis we want to show tournament name instead of "World" region
            type: TreeItemType.Region,
            selected: false,
            expanded: this.isNavigationExpanded,
            loading: false,
            icon: 'country-icon c' + regionFlag,
            class: hasEvents ? 'sport collapsible' : 'sport',
            children: [],
            events: [],
            collapsible: hasEvents,
            leftNavType: LeftNavType.Prematch,
            sportId: this.initialEvent.sport.id,
            toggleMarkets: this.state.showMarkets,
        };
    }

    private mapLeague(league: CountItem): TreeItem {
        return {
            id: league.id,
            name: league.name,
            type: TreeItemType.League,
            selected: false,
            expanded: this.expandedLeagues.has(league.id),
            loading: false,
            icon: '',
            count: league.counts.preMatch + league.counts.live,
            class: 'league',
            children: [],
            events: [],
            collapsible: true,
            leftNavType: LeftNavType.Prematch,
            sportId: this.initialEvent.sport.id,
            isVirtual: league.isVirtual,
            imageProfile: (league as unknown as LeagueTreeItem).imageProfile,
        };
    }

    private getTreeWithEvents(): Observable<BaseTreeItem> {
        const expandedLeaguesIds = (virtual: boolean) => {
            return Array.from(this.expandedLeagues.entries())
                .filter(([_, isVirtual]) => isVirtual === virtual)
                .map(([lId, _]) => lId);
        };

        const request: CountsFixturesRequest = {
            fixtureCountsRequest: {
                state: FixtureState.Latest,
                tagTypes: [TagType.Sport, TagType.Region, TagType.Tournament, TagType.Competition].join(','),
                extendedTags: [TagType.Sport, TagType.Region, TagType.Tournament, TagType.Competition].join(','),
                sortBy: SortByCriteria.Tags,
                sportIds: this.initialEvent.sport.id.toString(),
            },
            fixtureRequest: {
                fixtureTypes: FixtureType.Standard,
                state: FixtureState.Latest,
                offerMapping: OfferMapping.MainMarkets,
                sortBy: SortByCriteria.FixtureStage,
                competitionIds: toString(expandedLeaguesIds(false)),
                virtualCompetitionIds: toString(expandedLeaguesIds(true)),
                sportIds: this.initialEvent.sport.id.toString(),
            },
        };

        if (this.initialEvent.sport.id === SportConstant.Tennis) {
            request.fixtureCountsRequest.tournamentIds = this.initialEvent.region.id.toString();
        } else {
            request.fixtureCountsRequest.regionIds = this.initialEvent.region.id.toString();
        }

        return this.bettingService.getCountsFixtures(request).pipe(
            filter((response) => !!response),
            map((countsFixtures) => {
                const counts = sportModelMethods.fromTag(countsFixtures!.counts);
                const tree = this.mapTree(counts);
                const groupedEvents = this.eventMapperService.mapFixtures(countsFixtures!.fixtures);

                tree.children[0].children.filter((l) => !!groupedEvents[l.id]).forEach((l) => (l.events = this.mapNavEvents(groupedEvents[l.id])));

                return tree;
            }),
        );
    }

    private getValidTournaments(sportId: number): Tournament[] {
        return this.tournamentsConfig.tournaments.filter((tournament) => this.filterTournament(tournament, sportId));
    }

    private filterTournament(tournament: Tournament, sportId: number): boolean {
        return Boolean(tournament.navigation?.filter?.visible && tournament.leaguesSection?.sport?.id.toString() === sportId.toString());
    }
}
